/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from 'react';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Dropdown from '@mui/joy/Dropdown';

import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import CustomTable, { IColumn } from './CustomTable';
import { IInvoice } from '../interface/IInvoice';
import {
  calculateInvoicedTotalAmount,
  calculateListPrice,
  calculateProjectPrice,
  deutschNumberFormatter,
  getTimeLoggedStringFormat,
} from '../helper/utils';
import dayjs from 'dayjs';
import InvoicePdf from './InvoicePdf';
import invoiceApi from '../api/invoice';
import { pdf } from '@react-pdf/renderer';
import { Link } from '@mui/joy';
import { IOffer } from '../interface/IOffer';
import { useToast } from '../helper/ToastProvider';

function RowMenu({ sendInvoiceEmail, onClickXml, onClickPayment, onClickPdfWithoutVat }: any) {
  return (
    <Dropdown>
      <MenuButton slots={{ root: IconButton }} slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm' } }}>
        <MoreHorizRoundedIcon />
      </MenuButton>
      <Menu size='sm' sx={{ minWidth: 140 }}>
        <MenuItem onClick={onClickXml} color='primary'>
          XML
        </MenuItem>
        <MenuItem onClick={onClickPayment} color='primary'>
          Payment
        </MenuItem>
        <MenuItem onClick={onClickPdfWithoutVat} color='primary'>
          Pdf w/o VAT
        </MenuItem>
        <MenuItem onClick={sendInvoiceEmail} color='primary'>
          Send Email
        </MenuItem>
        <Divider />
        <MenuItem color='danger'>Delete</MenuItem>
      </Menu>
    </Dropdown>
  );
}

const getInvoicePdfBlob = async (invoice: IInvoice) => {
  const invoicePositions: any[] = [];
  invoice.invoiceDetails.forEach((item: any) => {
    const existing = invoicePositions.find((r) => r.position === item.offerPosition);

    if (existing) {
      existing.timeInvoiced += item.timeInvoiced;
    } else {
      invoicePositions.push({
        position: item.offerPosition,
        rate: item.rate,
        subject: item.epicLink,
        timeInvoiced: item.timeInvoiced,
      });
    }
  });

  invoicePositions.forEach((item: any) => {
    item.listPrice = calculateListPrice(item.timeInvoiced / 3600, invoice.offer?.[item.rate! as keyof IOffer] as number);
    item.projectPrice = calculateProjectPrice(
      item.timeInvoiced / 3600,
      invoice.offer?.[item.rate! as keyof IOffer] as number,
      invoice.offer?.discount
    );
  });
  invoicePositions.sort((a, b) => a.position - b.position);
  return pdf(<InvoicePdf invoice={invoice} invoicePositions={invoicePositions} offer={invoice.offer!} client={invoice.offer?.client!} />).toBlob();
};

const PdfDownloadButton = ({ invoiceId }: { invoiceId: string }) => {
  const handleDownload = async () => {
    const res = await invoiceApi.getInvoiceById(invoiceId);
    const invoice: IInvoice = res.data;
    const pdfBlob = await getInvoicePdfBlob(invoice);
    const url = window.URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${invoice.invoiceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return <Link onClick={handleDownload}>Pdf</Link>;
};

type Props = {
  invoices: IInvoice[];
  onDownloadXml: (invoiceId: string) => void;
  onDownloadExcel: (invoiceId: string) => void;
  onEmailSend: () => void;
  onPayment: (selInvoice: IInvoice) => void;
};

export default function InvoiceTable({ invoices, onDownloadXml, onDownloadExcel, onEmailSend, onPayment }: Props) {
  const { showToast } = useToast();
  const columns: IColumn<IInvoice>[] = [
    {
      id: 'invoiceId',
      disablePadding: false,
      label: 'ID',
      numeric: false,
      dataIndex: 'invoiceId',
      width: 70,
      sort: true,
      sortFunction: (a: IInvoice, b: IInvoice) => {
        // const lastDigitA = a.offerId[a.offerId.length - 1];
        // const lastDigitB = b.offerId[b.offerId.length - 1];
        // if (lastDigitA !== lastDigitB) {
        //   return lastDigitA.localeCompare(lastDigitB);
        // }
        const lastPartA = a.invoiceId.split('-')[1];
        const lastPartB = b.invoiceId.split('-')[1];
        return lastPartA.localeCompare(lastPartB);
      },
    },
    {
      id: 'offerId',
      disablePadding: false,
      label: 'Offer ID',
      numeric: false,
      dataIndex: 'offerId',
      width: 50,
      sort: true,
      sortFunction: (a: IOffer, b: IOffer) => {
        const lastDigitA = a.offerId[a.offerId.length - 1];
        const lastDigitB = b.offerId[b.offerId.length - 1];
        if (lastDigitA !== lastDigitB) {
          return lastDigitA.localeCompare(lastDigitB);
        }
        const firstThreeA = a.offerId.slice(0, 3);
        const firstThreeB = b.offerId.slice(0, 3);
        return firstThreeA.localeCompare(firstThreeB);
      },
    },
    {
      id: 'offerName',
      disablePadding: false,
      label: 'Offer Name',
      numeric: false,
      render: (row) => <Typography level='body-xs'>{row?.offer?.title || ''}</Typography>,
      width: 300,
      sort: true,
      sortFunction: (a: IInvoice, b: IInvoice) => {
        if (b.offer?.title! < a.offer?.title!) {
          return -1;
        }
        if (b.offer?.title! > a.offer?.title!) {
          return 1;
        }
        return 0;
      },
    },
    {
      id: 'invoiceFrom',
      disablePadding: false,
      label: 'Service Period',
      numeric: false,
      dataIndex: 'invoiceFrom',
      render: (row) => (
        <Typography level='body-xs'>
          {dayjs(row.invoiceFrom).format('DD.MM.YYYY') || ''} - {dayjs(row.invoiceTo).format('DD.MM.YYYY') || ''}
        </Typography>
      ),
      width: 120,
    },
    {
      id: 'amountNet',
      disablePadding: false,
      label: 'Total Net',
      currency: true,
      numeric: true,
      render: (row) => (
        <Typography level='body-xs'>
          {(row.offer &&
            `${deutschNumberFormatter(calculateInvoicedTotalAmount(row.invoiceDetails, row.offer?.DEV!, row.offer?.PM!, row.offer?.discount))} €`) ||
            ''}
        </Typography>
      ),
      sort: true,
      width: 80,
      sortFunction: (a: IInvoice, b: IInvoice) => {
        let aTotalGross = calculateInvoicedTotalAmount(a.invoiceDetails, a.offer?.DEV!, a.offer?.PM!, a.offer?.discount);
        let bTotalGross = calculateInvoicedTotalAmount(b.invoiceDetails, b.offer?.DEV!, b.offer?.PM!, b.offer?.discount);
        return aTotalGross - bTotalGross;
      },
    },
    {
      id: 'amountGross',
      disablePadding: false,
      label: 'Total Gross',
      currency: true,
      numeric: true,
      render: (row) => (
        <Typography level='body-xs'>
          {(row.offer &&
            `${deutschNumberFormatter(
              calculateInvoicedTotalAmount(row.invoiceDetails, row.offer?.DEV!, row.offer?.PM!, row.offer?.discount) * 1.19
            )} €`) ||
            ''}
        </Typography>
      ),
      width: 80,
      sort: true,
      sortFunction: (a: IInvoice, b: IInvoice) => {
        let aTotalGross = calculateInvoicedTotalAmount(a.invoiceDetails, a.offer?.DEV!, a.offer?.PM!, a.offer?.discount) * 1.19;
        let bTotalGross = calculateInvoicedTotalAmount(b.invoiceDetails, b.offer?.DEV!, b.offer?.PM!, b.offer?.discount) * 1.19;
        return aTotalGross - bTotalGross;
      },
    },
    {
      id: 'invoiceDate',
      disablePadding: false,
      label: 'Invoice Date',
      numeric: false,
      dataIndex: 'invoiceDate',
      render: (row) => <Typography level='body-xs'>{dayjs(row.invoiceDate).format('DD.MM.YYYY') || ''}</Typography>,
      sort: true,
      width: 70,
      sortFunction(a: IInvoice, b: IInvoice) {
        if (a.invoiceDate === null && b.invoiceDate === null) return 0;
        if (a.invoiceDate === null) return -1;
        if (b.invoiceDate === null) return 1;
        const dateA = new Date(a.invoiceDate).getTime();
        const dateB = new Date(b.invoiceDate).getTime();
        return dateA - dateB;
      },
    },
    {
      id: 'dueDate',
      disablePadding: false,
      label: 'Due Date',
      numeric: false,
      dataIndex: 'dueDate',
      render: (row) => <Typography level='body-xs'>{row.dueDate ? dayjs(row.dueDate).format('DD.MM.YYYY') : '-'}</Typography>,
      width: 70,
      sort: true,
      sortFunction(a: IInvoice, b: IInvoice) {
        if (a.dueDate === null && b.dueDate === null) return 0;
        if (a.dueDate === null) return -1;
        if (b.dueDate === null) return 1;
        const dateA = new Date(a.dueDate).getTime();
        const dateB = new Date(b.dueDate).getTime();
        return dateA - dateB;
      },
    },
    {
      id: 'dateOfPayment',
      disablePadding: false,
      label: 'Payment Date',
      numeric: false,
      dataIndex: 'dateOfPayment',
      render: (row) => (
        <>
          <Typography level='body-xs'>{row.dateOfPayment ? dayjs(row.dateOfPayment).format('DD.MM.YYYY') : '-'}</Typography>
        </>
      ),
      width: 80,
      sort: true,
      sortFunction(a: IInvoice, b: IInvoice) {
        if (a.dateOfPayment === null && b.dateOfPayment === null) return 0;
        if (a.dateOfPayment === null) return -1;
        if (b.dateOfPayment === null) return 1;
        const dateA = new Date(a.dateOfPayment).getTime();
        const dateB = new Date(b.dateOfPayment).getTime();
        return dateA - dateB;
      },
    },
    {
      id: 'status',
      disablePadding: false,
      label: 'Status',
      numeric: false,
      render: (row) => <Typography level='body-xs'>{row?.status || ''}</Typography>,
      width: 50,
      sort: true,
    },
    {
      id: 'actions',
      disablePadding: false,
      label: '',
      numeric: false,
      dataIndex: 'invoiceId',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* <PdfDownloadButton invoiceId={row.invoiceId} /> */}
          <Link onClick={() => onDownloadPdf(row.invoiceId)}>Pdf</Link>
          <Link onClick={() => onDownloadExcel(row.invoiceId)}>Excel</Link>
          <RowMenu
            onClickXml={() => onDownloadXml(row.invoiceId)}
            onClickPayment={() => onPayment(row)}
            onClickPdfWithoutVat={() => onDownloadPdfWithoutVat(row.invoiceId)}
            sendInvoiceEmail={() => sendInvoiceEmail(row.invoiceId)}
          />
        </Box>
      ),
      width: 100,
    },
  ];

  const onDownloadPdf = async (invoiceId: string) => {
    try {
      const response = await invoiceApi.downloadInvoicePdfFile(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Pdf:', error);
    }
  };

  const onDownloadPdfWithoutVat = async (invoiceId: string) => {
    try {
      const response = await invoiceApi.downloadInvoiceWithoutVatPdfFile(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Pdf:', error);
    }
  };

  const sendInvoiceEmail = async (invoiceId: string) => {
    try {
      await invoiceApi.sendInvoiceEmail(invoiceId);
      onEmailSend();
      showToast({
        message: 'Email Send Success',
        severity: 'success',
      });
    } catch (e) {
      showToast({
        message: 'Email Send Failed',
        severity: 'danger',
      });
    }
  };

  return (
    <React.Fragment>
      <CustomTable data={invoices} idKey='invoiceId' columns={columns} />
    </React.Fragment>
  );
}
